import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useCartCount } from '@/hooks/useCart'
import { FORMS, submitAuthForm } from '@/lib/authForm'
import SlideMenu from './SlideMenu'

/* Logo loaded from modules/website/public/assets/images/logo/Migration-Galleries-Logo.svg
   All styling lives in src/styles/components/header.css.
   State-driven properties (transform/opacity/cursor based on menu open
   state, sidebar reveal, signing-out spinner) are toggled via data-* on
   the relevant element so CSS selectors can match them. */

/* Shopping bag icon — Figma node I845:1749;270:1174 (cart-icon).
   40.928 × 51.09 viewBox: square body (40.928 × 40.928, 4px stroke,
   sitting at the bottom) + inverted-U handle (22.664 × 22.34, 4px
   stroke, centered horizontally on top). The cart count is painted
   over the body in alfarn-2 700 16px to match the design.
   Wrapped in an anchor to /cart. */
function CartBag({ count }: { count: number }) {
  if (count < 1) return null
  return (
    <a href="/cart" className="mig-cart-bag" aria-label={`Cart (${count} ${count === 1 ? 'item' : 'items'})`}>
      <svg
        className="mig-cart-bag-icon"
        width="40.928"
        height="51.09"
        viewBox="0 0 40.928 51.09"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {/* Handle (inverted-U ∩) — curve arches above body, legs descend
            through the top of the body. Mirror of the Figma handle SVG
            (bag-body.svg) so the closed end of the U is at the top of
            the icon and the open ends meet/cross the body's top edge. */}
        <path
          d="M11.13 24.34 V7.92 C11.13 4.65 13.78 2 17.05 2 H23.87 C27.15 2 29.80 4.65 29.80 7.92 V24.34"
          stroke="#000000"
          strokeWidth="4"
          fill="none"
        />
        {/* Body — 40.928 × 40.928 square outline, 4px stroke, bottom-aligned. */}
        <rect x="2" y="12.162" width="36.928" height="36.928" stroke="#000000" strokeWidth="4" fill="none" />
      </svg>
      <span className="mig-cart-bag-count" aria-hidden="true">{count}</span>
    </a>
  )
}

function HamburgerIcon() {
  return (
    <svg className="mig-hamburger-icon" width="33.314" height="44.73" viewBox="0 0 33.314 44.73" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0 44.73H33.314V33.7839H0V44.73Z" fill="black"/>
      <path d="M0 27.8383H33.314V16.8925H0V27.8383Z" fill="black"/>
      <path d="M0 10.946H33.314V0H0V10.946Z" fill="black"/>
    </svg>
  )
}

/* Figma: node I249:766;249:738 - Close icon (X) shown when menu is open, 34.227 x 34.227px */
function CloseIcon() {
  return (
    <svg className="mig-close-icon" width="34.227" height="34.227" viewBox="0 0 34.2274 34.227" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0.000366211 26.487L7.74037 34.227L34.2274 7.74L26.4874 0L0.000366211 26.487Z" fill="black"/>
      <path d="M26.487 34.227L34.227 26.487L7.74 0L0 7.74L26.487 34.227Z" fill="black"/>
    </svg>
  )
}

function MigrationGalleriesText() {
  return (
    <svg className="mig-galleries-text-svg" width="34.675" height="554.197" viewBox="0 0 34.6751 554.197" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M33.9625 30.8275L25.4125 30.8275V12.92C25.4125 11.21 24.7475 10.4975 23.6075 10.4975C22.2775 10.4975 21.6125 11.21 21.6125 12.9675V20.9C21.6125 26.98 17.955 31.4925 11.115 31.4925C4.085 31.4925 0.712502 26.98 0.712502 20.9L0.712502 1.61499H9.2625L9.2625 18.715C9.2625 19.9975 9.7375 21.09 11.1625 21.09C12.54 21.09 13.0625 19.9975 13.0625 18.715L13.0625 10.5925C13.0625 4.22747 16.6725 0 23.3225 0C29.6875 0 33.9625 4.22747 33.9625 10.5925V30.8275Z" fill="black"/>
      <path d="M33.9625 61.5233H0.712502L0.712502 33.3558H9.2625L9.2625 50.7882H13.0625L13.0625 33.3558H21.6125V50.7882H25.4125V33.3558H33.9625V61.5233Z" fill="black"/>
      <path d="M33.9625 75.9959H0.712502L0.712502 65.0709H33.9625V75.9959Z" fill="black"/>
      <path d="M33.9625 111.621H0.712502L0.712502 90.1984C0.712502 82.9784 4.7975 78.3709 11.6375 78.3709C16.2925 78.3709 19.475 80.4609 20.995 84.0234L33.9625 78.8934V90.4359L22.135 94.6159V101.503H33.9625V111.621ZM14.7725 101.503L14.7725 92.7159C14.7725 90.7209 14.06 89.3434 11.97 89.3434C9.9275 89.3434 9.2625 90.7209 9.2625 92.7159L9.2625 101.503H14.7725Z" fill="black"/>
      <path d="M33.9625 142.839H0.712502L0.712502 114.672H9.2625L9.2625 132.104H13.0625L13.0625 114.672H21.6125V132.104H25.4125V114.672H33.9625V142.839Z" fill="black"/>
      <path d="M33.9625 169.28H0.712502L0.712502 158.355H25.4125V145.197H33.9625V169.28Z" fill="black"/>
      <path d="M33.9625 195.72H0.712502L0.712502 184.795H25.4125V171.638H33.9625V195.72Z" fill="black"/>
      <path d="M33.9625 232.451L0.712502 224.091L0.712502 205.946L33.9625 197.586V208.463L28.7375 209.746V220.291L33.9625 221.573V232.451ZM9.2625 215.588L20.5675 218.343V211.741L9.2625 214.496V215.588Z" fill="black"/>
      <path d="M34.675 249.01C34.675 259.08 27.8825 266.395 17.29 266.395C6.7925 266.395 0 259.08 0 249.01C0 240.412 4.845 233.81 13.0625 232.052V243.642C11.4475 244.735 10.26 246.492 10.26 248.962C10.26 252.905 12.825 255.565 17.3375 255.565C22.325 255.565 24.415 252.715 24.415 248.962C24.415 246.207 23.2275 244.07 20.425 243.5V250.91H16.7675L16.7675 231.72C27.835 231.72 34.675 239.177 34.675 249.01Z" fill="black"/>
      <path d="M33.9625 314.099H0.712502L0.712502 302.699L18.715 290.444V290.111H0.712502L0.712502 279.899H33.9625V291.299L16.0075 303.506V303.886H33.9625V314.099Z" fill="black"/>
      <path d="M34.675 334.304C34.675 344.374 28.2625 352.164 17.29 352.164C6.7925 352.164 0 344.374 0 334.304C0 324.234 6.7925 316.444 17.29 316.444C28.2625 316.444 34.675 324.234 34.675 334.304ZM24.3675 334.399C24.3675 330.361 21.4225 327.701 17.29 327.701C13.205 327.701 10.26 330.361 10.26 334.399C10.26 338.436 13.205 341.096 17.29 341.096C21.4225 341.096 24.3675 338.436 24.3675 334.399Z" fill="black"/>
      <path d="M33.9625 365.449H0.712502L0.712502 354.524H33.9625V365.449Z" fill="black"/>
      <path d="M33.9625 387.763H9.2625V397.263H0.712502L0.712502 367.338H9.2625V376.838H33.9625V387.763Z" fill="black"/>
      <path d="M33.9625 428.991L0.712502 420.631L0.712502 402.486L33.9625 394.126V405.004L28.7375 406.286V416.831L33.9625 418.114V428.991ZM9.2625 412.129L20.5675 414.884V408.281L9.2625 411.036V412.129Z" fill="black"/>
      <path d="M33.9625 462.49H0.712502L0.712502 441.068C0.712502 433.848 4.7975 429.24 11.6375 429.24C16.2925 429.24 19.475 431.33 20.995 434.893L33.9625 429.763V441.305L22.135 445.485V452.373H33.9625V462.49ZM14.7725 452.373L14.7725 443.585C14.7725 441.59 14.06 440.213 11.97 440.213C9.9275 440.213 9.2625 441.59 9.2625 443.585L9.2625 452.373H14.7725Z" fill="black"/>
      <path d="M34.675 482.149C34.675 492.219 27.8825 499.534 17.29 499.534C6.7925 499.534 0 492.219 0 482.149C0 473.552 4.845 466.949 13.0625 465.192V476.782C11.4475 477.874 10.26 479.632 10.26 482.102C10.26 486.044 12.825 488.704 17.3375 488.704C22.325 488.704 24.415 485.854 24.415 482.102C24.415 479.347 23.2275 477.209 20.425 476.639V484.049H16.7675L16.7675 464.859C27.835 464.859 34.675 472.317 34.675 482.149Z" fill="black"/>
      <path d="M33.9625 512.82H0.712502L0.712502 501.895H33.9625V512.82Z" fill="black"/>
      <path d="M33.9625 554.197H0.712502L0.712502 543.889L11.2575 535.529V535.054L0.712502 526.694L0.712502 516.387H33.9625V526.837H14.82V527.312L24.89 535.054V535.529L14.82 543.272V543.747H33.9625V554.197Z" fill="black"/>
    </svg>
  )
}

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [sidebarRevealed, setSidebarRevealed] = useState(false)
  const { isAuthenticated, firstName, displayName } = useAuth()
  const cartCount = useCartCount()

  // Mutually exclusive: opening one of the two menus auto-closes the other.
  function toggleMenu() {
    setMenuOpen((v) => {
      const next = !v
      if (next) setUserMenuOpen(false)
      return next
    })
  }

  // Sidebar text reveal animation: bottom-to-top on first load, then fixed
  useEffect(() => {
    const timer = setTimeout(() => setSidebarRevealed(true), 600)
    return () => clearTimeout(timer)
  }, [])

  return (
    <>
      {/* Top Nav - fixed bar, full width. Figma: node 270:1427 */}
      <nav className="mig-top-nav" aria-label="Top bar">
        {/* Figma: Poppins Regular 27px, tracking -0.3164px.
            Logged out: SIGN IN link to the auth flow.
            Logged in: user's first name with a click-to-toggle Sign out dropdown. */}
        {isAuthenticated ? (
          <UserMenu
            firstName={firstName}
            displayName={displayName}
            open={userMenuOpen}
            setOpen={setUserMenuOpen}
            onBeforeOpen={() => setMenuOpen(false)}
          />
        ) : (
          <a
            href={`/sign-in${
              typeof window !== 'undefined' && window.location.pathname !== '/sign-in'
                ? `?return_to=${encodeURIComponent(window.location.pathname + window.location.search)}`
                : ''
            }`}
            className="mig-sign-in"
          >
            SIGN IN
          </a>
        )}

        {/* Logo - Figma: node 249:572, 156.56 x 106.218px. Links to home. */}
        <a href="/" className="mig-logo-link" aria-label="Migration Galleries home">
          <img
            src="/assets/modules/website/images/logo/Migration-Galleries-Logo.svg"
            alt="Migration Galleries"
            className="mig-logo"
            fetchPriority="high"
            decoding="async"
          />
        </a>
      </nav>

      {/* Sidebar - fixed left. Figma: node 183:37, 162px wide, p-24, bg #F9F9FA */}
      <aside className="mig-sidebar">
        <div className="mig-sidebar-top">
          {/* Cart bag — Figma node 845:1749 (cart-wrapper). Only rendered
              when the cart has at least one item; sits above the burger. */}
          <CartBag count={cartCount} />

          {/* Hamburger / Close icon */}
          <button
            className="mig-menu-toggle"
            onClick={toggleMenu}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            data-open={menuOpen}
          >
            {menuOpen ? <CloseIcon /> : <HamburgerIcon />}
          </button>
        </div>

        {/* "MIGRATION GALLERIES" - reveals bottom-to-top on first load */}
        <div className="mig-sidebar-text" data-revealed={sidebarRevealed}>
          <MigrationGalleriesText />
        </div>
      </aside>

      <SlideMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  )
}

/* ---------- User menu (dropdown for authenticated users) ---------- */
function UserMenu({
  firstName,
  displayName,
  open,
  setOpen,
  onBeforeOpen,
}: {
  firstName: string | null
  displayName: string | null
  open: boolean
  setOpen: (v: boolean) => void
  onBeforeOpen?: () => void
}) {
  const [signingOut, setSigningOut] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  // Close dropdown on outside click and Escape.
  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  async function handleSignOut() {
    if (!FORMS.signOut) {
      window.location.href = '/sign-out'
      return
    }
    setSigningOut(true)
    try {
      const { redirectTo } = await submitAuthForm('signOut', {})
      window.location.href = redirectTo || '/sign-out'
    } catch {
      window.location.href = '/sign-out'
    }
  }

  const label = (firstName?.trim() || displayName?.trim() || 'Account').toUpperCase()

  return (
    <div ref={wrapperRef} className="mig-user-menu">
      <button
        type="button"
        className="mig-user-menu-toggle"
        onClick={() => {
          if (!open) onBeforeOpen?.()
          setOpen(!open)
        }}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span>{label}</span>
        {/* Same chevron used by the marketplace filter sections — points
            down when the dropdown is closed and rotates 180deg up while
            it is open. */}
        <span aria-hidden="true" className="mig-user-menu-chevron" data-open={open}>
          <svg viewBox="0 0 27.912 16.419" width="28" height="16" fill="currentColor">
            <path d="M13.956 7.966L6.771 16.419H0L13.956 0L27.912 16.419H21.141L13.956 7.966Z" />
          </svg>
        </span>
      </button>
      {open && (
        <div role="menu" className="mig-user-menu-dropdown">
          <button
            type="button"
            role="menuitem"
            className="mig-user-menu-signout"
            onClick={handleSignOut}
            disabled={signingOut}
            data-pending={signingOut}
          >
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      )}
    </div>
  )
}
